/**
 * Payment API Service
 *
 * Handles all Stripe-related frontend operations:
 * - Creating checkout sessions and redirecting to Stripe
 * - Fetching the user's credits and subscription status
 */
import api from './api';

export const paymentAPI = {
  /**
   * Fetches the current user's payment status.
   * Returns { credits, is_subscribed, stripe_customer_id, cancel_at_period_end, current_period_end }
   */
  getStatus: () => api.get('/payments/status'),

  /**
   * Creates a Stripe Checkout Session for the given price_id
   * and redirects the browser to the Stripe-hosted checkout page.
   *
   * @param {string} priceId - The Stripe Price ID to purchase
   * @param {object} presaveState - Optional { jd, analysis } to restore on return
   */
  redirectToCheckout: async (priceId, presaveState = {}) => {
    const response = await api.post('/payments/create-checkout-session', { price_id: priceId });
    const checkoutUrl = response.data?.checkout_url;
    if (!checkoutUrl) {
      throw new Error('Could not create checkout session. Please try again.');
    }
    // Save current dashboard state so it can be restored after payment
    try {
      sessionStorage.setItem('rz_presave', JSON.stringify(presaveState));
    } catch (_) {}
    // Redirect the browser to Stripe Checkout
    window.location.href = checkoutUrl;
  },

  /**
   * Cancels the user's subscription at the end of the current billing period.
   * Returns { status: "canceling", access_until: "ISO date" }
   */
  cancelSubscription: () => api.post('/payments/cancel-subscription'),

  /**
   * Re-activates a subscription that was scheduled for cancellation.
   * Returns { status: "active" }
   */
  reactivateSubscription: () => api.post('/payments/reactivate-subscription'),
};

export default paymentAPI;
